import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { rangeStart, type DashboardRange } from "./DateRangeSelector";

/** Apply the active-workspace scope to a supabase query builder. */
export const scopeToWorkspace = <T>(query: T): T => {
  const workspaceId = getActiveWorkspaceId();
  if (!workspaceId) return query;
  return (query as unknown as { eq: (column: string, value: string) => T }).eq(
    "workspace_id",
    workspaceId,
  );
};

export interface DealRow {
  id: number;
  name: string;
  amount: number | null;
  stage: string;
  created_at: string;
  archived_at: string | null;
  expected_closing_date: string | null;
}

/**
 * Real dashboard data. RLS scopes every query to the user's workspace, so no
 * client-side tenant filtering exists here (or anywhere).
 */
export const useDashboardData = (range: DashboardRange) => {
  const workspaceId = getActiveWorkspaceId();

  const dealsQuery = useQuery({
    queryKey: ["gravun", "dashboard", "deals", workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<DealRow[]> => {
      const { data, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("deals")
          .select(
            "id, name, amount, stage, created_at, archived_at, expected_closing_date",
          ),
      ).limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const start = rangeStart(range);
  const contactsQuery = useQuery({
    queryKey: ["gravun", "dashboard", "leads-added", range, workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("contacts")
          .select("id", { count: "exact", head: true }),
      ).gte("first_seen", start.toISOString());
      if (error) throw error;
      return count ?? 0;
    },
  });

  const totalContactsQuery = useQuery({
    queryKey: ["gravun", "dashboard", "contacts-total", workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("contacts")
          .select("id", { count: "exact", head: true }),
      );
      if (error) throw error;
      return count ?? 0;
    },
  });

  const invoicesQuery = useQuery({
    queryKey: ["gravun", "dashboard", "invoices", workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<InvoiceRow[]> => {
      const { data, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("invoices")
          .select("id, status, total_cents, balance_cents, issued_at"),
      ).limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const paymentsQuery = useQuery({
    queryKey: ["gravun", "dashboard", "payments", range, workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<PaymentRow[]> => {
      const { data, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("payments")
          .select("id, amount_cents, received_at"),
      )
        .gte("received_at", start.toISOString().slice(0, 10))
        .limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const tasksDueQuery = useQuery({
    queryKey: ["gravun", "dashboard", "tasks-due", workspaceId],
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const { count, error } = await scopeToWorkspace(
        getSupabaseClient()
          .from("tasks")
          .select("id", { count: "exact", head: true }),
      )
        .is("done_date", null)
        .lte("due_date", endOfToday.toISOString());
      if (error) throw error;
      return count ?? 0;
    },
  });

  return {
    deals: dealsQuery.data ?? [],
    invoices: invoicesQuery.data ?? [],
    payments: paymentsQuery.data ?? [],
    leadsAdded: contactsQuery.data ?? 0,
    totalContacts: totalContactsQuery.data ?? 0,
    tasksDue: tasksDueQuery.data ?? 0,
    isPending: dealsQuery.isPending || contactsQuery.isPending,
  };
};

export interface InvoiceRow {
  id: string;
  status: string;
  total_cents: number;
  balance_cents: number;
  issued_at: string | null;
}

export interface PaymentRow {
  id: string;
  amount_cents: number;
  received_at: string;
}

/** Invoice statuses that count toward money still owed to the business. */
const OPEN_INVOICE_STATUSES = new Set(["issued", "partially_paid", "overdue"]);

export interface DashboardMetrics {
  pipelineValue: { value: string; helper: string };
  outstanding: { value: string; helper: string };
  revenue: { value: string; helper: string };
  leadsAdded: { value: string; helper: string };
  contacts: { value: string; helper: string };
  tasksDue: { value: string; helper: string };
}

export const formatMoney = (amount: number, currency: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: amount >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: amount >= 10_000 ? 1 : 0,
  }).format(amount);

/**
 * Compute the KPI row from real workspace data. Revenue is CASH COLLECTED
 * (payments received in the selected range) and Outstanding is the open
 * balance across issued/partially-paid/overdue invoices, both come from the
 * billing modules, never from deal guesses. Nothing is fabricated.
 */
export const computeMetrics = (
  deals: DealRow[],
  invoices: InvoiceRow[],
  payments: PaymentRow[],
  leadsAdded: number,
  totalContacts: number,
  tasksDue: number,
  range: DashboardRange,
  currency: string,
  wonStages: string[],
): DashboardMetrics => {
  void range; // range already applied server-side to the payments query
  const isWon = (deal: DealRow) => wonStages.includes(deal.stage);
  const isLost = (deal: DealRow) => deal.stage === "lost";

  const open = deals.filter(
    (deal) => !deal.archived_at && !isWon(deal) && !isLost(deal),
  );
  const openTotal = open.reduce((sum, d) => sum + (d.amount ?? 0), 0);

  // Payments are pre-filtered to the selected range by the query.
  const collectedCents = payments.reduce((sum, p) => sum + p.amount_cents, 0);

  const openInvoices = invoices.filter((invoice) =>
    OPEN_INVOICE_STATUSES.has(invoice.status),
  );
  const outstandingCents = openInvoices.reduce(
    (sum, invoice) => sum + invoice.balance_cents,
    0,
  );

  return {
    pipelineValue: {
      value: formatMoney(openTotal, currency),
      helper: `${open.length} open ${open.length === 1 ? "deal" : "deals"}`,
    },
    outstanding: {
      value: formatMoney(outstandingCents / 100, currency),
      helper:
        openInvoices.length > 0
          ? `${openInvoices.length} unpaid ${openInvoices.length === 1 ? "invoice" : "invoices"}`
          : "No unpaid invoices",
    },
    revenue: {
      value:
        payments.length > 0
          ? formatMoney(collectedCents / 100, currency)
          : "n/a",
      helper:
        payments.length > 0
          ? `${payments.length} ${payments.length === 1 ? "payment" : "payments"} collected`
          : "No payments collected",
    },
    leadsAdded: {
      value: String(leadsAdded),
      helper: leadsAdded > 0 ? "New contacts" : "No new leads",
    },
    contacts: {
      value: String(totalContacts),
      helper: "In this workspace",
    },
    tasksDue: {
      value: String(tasksDue),
      helper: tasksDue > 0 ? "Due today or overdue" : "All caught up",
    },
  };
};
